/**
 * Partner referral capture.
 * Reads ?ref=CODE from the URL, validates against active partners,
 * and stores it in localStorage. After login, calls recordReferral()
 * to insert a row in time_partner_referrals (RLS: auth.uid() = user_id).
 */
import { supabase } from "@/integrations/supabase/client";

const STORAGE_KEY = "addonnordic_time_ref";

interface StoredRef {
  code: string;
  partnerId: string;
  capturedAt: string;
}

export async function captureRefFromUrl(): Promise<void> {
  if (typeof window === "undefined") return;
  const params = new URLSearchParams(window.location.search);
  const code = params.get("ref");
  if (!code) return;
  if (localStorage.getItem(STORAGE_KEY)) return;

  const { data, error } = await supabase.rpc("resolve_partner_code", { _code: code });
  if (error || !data || data.length === 0) return;

  const partner = data[0];
  const stored: StoredRef = {
    code: code.toUpperCase(),
    partnerId: partner.id,
    capturedAt: new Date().toISOString(),
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
}

export function getStoredRef(): StoredRef | null {
  if (typeof window === "undefined") return null;
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try { return JSON.parse(raw) as StoredRef; } catch { return null; }
}

export async function recordReferralForUser(userId: string): Promise<void> {
  const ref = getStoredRef();
  if (!ref) return;

  const { error } = await supabase.from("time_partner_referrals").insert({
    user_id: userId,
    partner_id: ref.partnerId,
  });
  // 23505 = unique violation: already recorded. Treat as success.
  if (!error || (error as any).code === "23505") {
    localStorage.removeItem(STORAGE_KEY);
  }
}
